<template>
    <div v-if="debug" class="fixed left-0 top-[500px] z-50 max-w-[400px] mx-auto p-4 bg-black">
        <div class="text-center mb-4">
            <h3 class="text-xl text-gray-300 mb-2">Timer: {{ formatTime(currentTime) }}</h3>
            <div class="flex flex-col gap-2 mb-2">
                <p class="m-0 text-gray-600 text-sm">Limit: {{ formatTime(timeLimit) }}</p>
                <p class="m-0 text-gray-600 text-sm">Remaining: {{ formatTime(remainingTime) }}</p>
            </div>
        </div>

        <div class="text-center font-bold">
            <span :class="statusClass">{{ statusText }}</span>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'

const props = defineProps({
    timeLimit: {
        type: Number,
        required: true,
        validator: (value) => value > 0
    },
    storageKey: {
        type: String,
        default: 'app-timeout'
    },
    autoStart: {
        type: Boolean,
        default: true
    },
    debug: {
        type: Boolean,
        default: false
    }
})

const emit = defineEmits([
    'time-limit-reached',
    'timer-tick',
    'timer-started',
    'timer-paused',
    'timer-reset',
    'timer-restarted'
])

const currentTime = ref(0)
const isRunning = ref(false)
const intervalId = ref(null)
const startTimestamp = ref(null)
const wasPausedByVisibility = ref(false)

// Computed properties
const remainingTime = computed(() => {
    return Math.max(0, props.timeLimit - currentTime.value)
})

const isCompleted = computed(() => {
    return currentTime.value >= props.timeLimit
})

const statusText = computed(() => {
    if (isCompleted.value) return 'Time limit reached!'
    if (isRunning.value) return 'Running...'
    return 'Starting...'
})

const statusClass = computed(() => {
    if (isCompleted.value) return 'status-completed'
    if (isRunning.value) return 'status-running'
    return 'status-ready'
})

// Storage functions
const saveToStorage = () => {
    const timerData = {
        currentTime: currentTime.value,
        isRunning: isRunning.value,
        startTimestamp: startTimestamp.value,
        timeLimit: props.timeLimit,
        lastSaved: Date.now(),
        wasPausedByVisibility: wasPausedByVisibility.value
    }
    localStorage.setItem(props.storageKey, JSON.stringify(timerData))
}

const loadFromStorage = () => {
    try {
        const stored = localStorage.getItem(props.storageKey)
        if (!stored) return false

        const timerData = JSON.parse(stored)

        // Check if the stored timer is for the same time limit
        if (timerData.timeLimit !== props.timeLimit) {
            return false
        }

        // Restore the pause state
        wasPausedByVisibility.value = timerData.wasPausedByVisibility || false

        // If timer was running and NOT paused by visibility, calculate elapsed time
        if (timerData.isRunning && timerData.startTimestamp && !timerData.wasPausedByVisibility) {
            const now = Date.now()
            const elapsedSinceStart = Math.floor((now - timerData.startTimestamp) / 1000)
            currentTime.value = Math.min(elapsedSinceStart, props.timeLimit)

            // If we haven't reached the limit, continue running
            if (currentTime.value < props.timeLimit) {
                isRunning.value = true
                startTimestamp.value = timerData.startTimestamp
            } else {
                isRunning.value = false
                currentTime.value = props.timeLimit
            }
        } else {
            // Timer was not running or was paused by visibility, restore the saved time
            currentTime.value = timerData.currentTime
            isRunning.value = false
        }

        return true
    } catch (error) {
        console.error('Error loading timer from storage:', error)
        return false
    }
}

const clearStorage = () => {
    localStorage.removeItem(props.storageKey)
}

// Timer functions
const startTimer = () => {
    if (isCompleted.value) return

    isRunning.value = true
    wasPausedByVisibility.value = false
    startTimestamp.value = Date.now() - (currentTime.value * 1000)

    intervalId.value = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTimestamp.value) / 1000)
        currentTime.value = Math.min(elapsed, props.timeLimit)

        emit('timer-tick', currentTime.value)

        if (currentTime.value >= props.timeLimit) {
            stopTimer()
            emit('time-limit-reached', currentTime.value)
        }
    }, 1000)

    emit('timer-started', currentTime.value)
}

const pauseTimer = () => {
    isRunning.value = false
    if (intervalId.value) {
        clearInterval(intervalId.value)
        intervalId.value = null
    }
    emit('timer-paused', currentTime.value)
}

const stopTimer = () => {
    pauseTimer()
}

const resetTimer = () => {
    stopTimer()
    currentTime.value = 0
    startTimestamp.value = null
    wasPausedByVisibility.value = false
    clearStorage()
    emit('timer-reset')
}

// Method to restart timer from external trigger
const restartTimer = () => {
    stopTimer()
    currentTime.value = 0
    startTimestamp.value = null
    wasPausedByVisibility.value = false
    clearStorage()
    startTimer()
    emit('timer-restarted')
}

// Utility function
const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
}

// Page visibility handlers
const handleVisibilityChange = () => {
    if (document.hidden) {
        // Page is hidden (user switched tabs, minimized, etc.)
        if (isRunning.value) {
            wasPausedByVisibility.value = true
            pauseTimer()
        }
    } else {
        // Page is visible again
        if (!isCompleted.value && wasPausedByVisibility.value) {
            wasPausedByVisibility.value = false
            startTimer()
        }
    }
}

// Watchers
watch([currentTime, isRunning, wasPausedByVisibility], () => {
    saveToStorage()
})

// Watch for external storage clearing
watch(() => localStorage.getItem(props.storageKey), (newValue, oldValue) => {
    // If storage was cleared externally while timer was running
    if (newValue === null && oldValue !== null && (isRunning.value || currentTime.value > 0)) {
        console.log('Storage cleared externally, restarting timer...')
        restartTimer()
    }
}, { immediate: false })

// Lifecycle hooks
onMounted(() => {
    // Try to load from storage first
    const loaded = loadFromStorage()

    // If nothing was loaded or autoStart is true, start the timer
    if (!loaded || props.autoStart) {
        startTimer()
    }

    // If we loaded a running timer, start the interval
    if (isRunning.value && currentTime.value < props.timeLimit) {
        startTimer()
    }

    // Check if timer was already completed when loaded
    if (isCompleted.value) {
        emit('time-limit-reached', currentTime.value)
    }

    // Add page visibility event listener
    document.addEventListener('visibilitychange', handleVisibilityChange)
})

onUnmounted(() => {
    if (intervalId.value) {
        clearInterval(intervalId.value)
    }

    // Remove page visibility event listener
    document.removeEventListener('visibilitychange', handleVisibilityChange)
})

// Expose methods for parent component
defineExpose({
    restartTimer,
    resetTimer,
    pauseTimer,
    startTimer
})

</script>

<style scoped>
.status-ready {
    color: #6c757d;
}

.status-running {
    color: #28a745;
}

.status-completed {
    color: #dc3545;
}
</style>
